import React, { ChangeEvent, FC, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ResourceTypeLink } from "@evergis/api";
import {
  ActionsGroup,
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  IconButton,
  WaitingButton,
  Input,
  Icon,
} from "@evergis/ui";

import { ModalDialog } from "../DialogComponents/ModalDialog";
import { ProjectWms } from "./components/ProjectWms";
import { LayerWms } from "./components/LayerWms";
import { AdditionalInfoLink, Contents, DialogDescription, UsersLabel } from "./styled";
import { dialogResourcesSelector } from "../../../ducks/dialogs/selectors";
import { useDialogReset } from "../../../hooks/useDialogs";
import { useIsShared } from "../../../hooks/api/useIsShared";

export const WmsLink: FC = () => {
  const { t } = useTranslation();
  const isShared = useIsShared();
  const [name, resourceType, alias] = useSelector(dialogResourcesSelector);
  const closeDialog = useDialogReset();
  const [link, setLink] = useState<string>(`${window.location.protocol}//${window.location.host}/sp/wms/${name}`);
  const [isLoading, setIsLoading] = useState<boolean>(!isShared);

  const dialogTitle = useMemo(() => {
    switch (resourceType) {
      case ResourceTypeLink.Project:
        return `${t("wmsLink.linkToMap", { ns: "dialogs" })} ${alias || name}`;
      case ResourceTypeLink.Layer:
        return `${t("wmsLink.linkToLayer", { ns: "dialogs" })} ${alias || name}`;
      default:
        return "";
    }
  }, [alias, name, resourceType, t]);

  const getContents = useCallback(() => {
    switch (resourceType) {
      case ResourceTypeLink.Project:
        return <ProjectWms projectName={name} setIsLoading={setIsLoading} />;
      case ResourceTypeLink.Layer:
        return <LayerWms layerName={name} setIsLoading={setIsLoading} />;
      default:
        return null;
    }
  }, [resourceType, name]);

  return (
    <ModalDialog maxWidth="62rem" minHeight="18rem">
      <DialogTitle style={{ flexDirection: "column", alignItems: "flex-start" }}>
        <Flex style={{ width: "100%" }}>
          <ActionsGroup>{dialogTitle}</ActionsGroup>
          <ActionsGroup>
            <IconButton kind="close" onClick={closeDialog} />
          </ActionsGroup>
        </Flex>
        <DialogDescription>{t("wmsLink.dialogDescription", { ns: "dialogs" })}</DialogDescription>
      </DialogTitle>
      <DialogContent>
        <Contents>
          <Input
            label={t("wmsLink.link", { ns: "dialogs" })}
            value={link}
            onChange={(evt: ChangeEvent<HTMLInputElement>) => setLink(evt.target.value)}
            width="100%"
          >
            <IconButton kind={"copy"} onClick={() => navigator.clipboard.writeText(link)} />
          </Input>
          {!isShared && (
            <>
              <br />
              <UsersLabel>{t("wmsLink.users", { ns: "dialogs" })}</UsersLabel>
              {getContents()}
            </>
          )}
        </Contents>
      </DialogContent>
      <DialogActions>
        <AdditionalInfoLink href={"/external/wms-qgis"}>
          <Icon kind="info" />
          {t("wmsLink.wmsQgis", { ns: "dialogs" })}
        </AdditionalInfoLink>
        <ActionsGroup>
          <WaitingButton
            primary
            onClick={closeDialog}
            style={{ minWidth: "7rem", textAlign: "center" }}
            isWaiting={isLoading}
          >
            {t("actions.ok", { ns: "common" })}
          </WaitingButton>
        </ActionsGroup>
      </DialogActions>
    </ModalDialog>
  );
};
